import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table'

export interface PersistedTableState {
  columnFilters: ColumnFiltersState
  columnVisibility: VisibilityState
  sorting: SortingState
  extras?: Record<string, unknown>
}

interface TableStateStore {
  version: 1
  states: Record<string, PersistedTableState>
  saveState: (key: string, state: PersistedTableState) => void
  patchState: (key: string, patch: Partial<PersistedTableState>) => void
  readState: (key: string) => PersistedTableState | undefined
  clearState: (key: string) => void
}

export function makeTableStateKey(userId: string | undefined, pageKey: string): string {
  return `${userId ?? 'anonymous'}:${pageKey}`
}

const LEGACY_PREFIX = 'dt-cols-'

function readLegacyVisibility(storageKey: string): VisibilityState | undefined {
  if (typeof localStorage === 'undefined') return undefined
  try {
    const raw = localStorage.getItem(`${LEGACY_PREFIX}${storageKey}`)
    return raw ? (JSON.parse(raw) as VisibilityState) : undefined
  } catch {
    return undefined
  }
}

function removeLegacyVisibility(storageKey: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.removeItem(`${LEGACY_PREFIX}${storageKey}`)
  } catch {
    /* ignore */
  }
}

function emptyState(): PersistedTableState {
  return { columnFilters: [], columnVisibility: {}, sorting: [] }
}

export const useTableStateStore = create<TableStateStore>()(
  persist(
    (set, get) => ({
      version: 1,
      states: {},
      saveState: (key, state) =>
        set((s) => ({ states: { ...s.states, [key]: state } })),
      patchState: (key, patch) =>
        set((s) => {
          const current = s.states[key] ?? emptyState()
          return { states: { ...s.states, [key]: { ...current, ...patch } } }
        }),
      readState: (key) => get().states[key],
      clearState: (key) =>
        set((s) => {
          if (!(key in s.states)) return s
          const next: Record<string, PersistedTableState> = {}
          for (const k of Object.keys(s.states)) {
            if (k !== key) next[k] = s.states[k]!
          }
          return { states: next }
        }),
    }),
    { name: 'th-table-state', version: 1 },
  ),
)

/**
 * Imports a legacy `dt-cols-<storageKey>` visibility blob into the new store
 * under the given composite key, then deletes the old key. Idempotent.
 */
export function migrateLegacyVisibility(compositeKey: string, storageKey: string): void {
  const legacy = readLegacyVisibility(storageKey)
  if (!legacy) return
  const store = useTableStateStore.getState()
  const existing = store.readState(compositeKey)
  if (existing) {
    store.saveState(compositeKey, {
      ...existing,
      columnVisibility: { ...legacy, ...existing.columnVisibility },
    })
  } else {
    store.saveState(compositeKey, { ...emptyState(), columnVisibility: legacy })
  }
  removeLegacyVisibility(storageKey)
}

export function isPersistedStateDefault(state: PersistedTableState | undefined): boolean {
  if (!state) return true
  const noFilters = state.columnFilters.length === 0
  const noSort = state.sorting.length === 0
  const noVisibility = Object.keys(state.columnVisibility).length === 0
  const noExtras = !state.extras || Object.keys(state.extras).length === 0
  return noFilters && noSort && noVisibility && noExtras
}
